class VultureCart {
  constructor() {
    this.drawerId = 'VultureDrawerPrimary';
    this.refreshTimeout = null;
    this.pendingRequests = 0;
    this.init();
  }

  init() {
    this.bindEvents();
    this.refresh();
  }

  getDrawer() {
    return document.getElementById(this.drawerId);
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (target.closest('.cart-drawer-close') || target.closest('.cart-drawer-overlay')) {
        this.close();
      }
      const openBtn = target.closest('[href="/cart"]') || target.closest('.cart-toggle');
      if (openBtn) {
        e.preventDefault();
        this.open();
      }
      const qtyBtn = target.closest('.qty-minus, .qty-plus');
      if (qtyBtn) {
        const key = qtyBtn.dataset.key;
        const change = qtyBtn.classList.contains('qty-plus') ? 1 : -1;
        this.handleQtyChange(qtyBtn, key, change);
      }
    });

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action$="/cart/add"]');
      if (!form) return;

      // Intercept if it has our data attribute or if it's inside a product-form 
      // (which we want to handle via Vulture's drawer instead of the default notification)
      if (form.hasAttribute('data-vulture-ajax-cart') || form.closest('product-form') || form.classList.contains('quick-add-form')) {
        e.preventDefault();
        this.addFromForm(form);
      }
    });
  }

  async refresh() {
    this.pendingRequests++;
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (err) {
      console.error('Failure in cart retrieval:', err);
    } finally {
      this.pendingRequests--;
    }
  }

  handleQtyChange(btn, key, change) {
    const row = btn.closest('.cart-item-row');
    const qtySpan = row?.querySelector('.qty-display');
    if (!qtySpan) return;

    let currentQty = parseInt(qtySpan.innerText);
    let newQty = Math.max(0, currentQty + change);
    
    // 1. Optimistic Qty Update
    qtySpan.innerText = newQty;
    
    // 2. Optimistic Total Calculation (Approximate)
    this.updateTotalOptimistically(row, change);

    // 3. High-Frequency Sync (200ms)
    if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = null;
      this.updateQty(key, newQty);
    }, 200); 
  }

  updateTotalOptimistically(row, change) {
    const totalContainer = document.getElementById('CartDrawerTotal');
    if (!totalContainer) return;

    let itemPriceCents = row.dataset.priceCents ? parseInt(row.dataset.priceCents, 10) : null;
    if (isNaN(itemPriceCents) || itemPriceCents === null) {
      const priceText = row.querySelector('.item-price')?.innerText;
      if (!priceText) return;
      const parsed = parseFloat(priceText.replace(/[^0-9.]/g, ''));
      itemPriceCents = Math.round(parsed * 100);
    }

    const currentTotalParsed = parseFloat(totalContainer.innerText.replace(/[^0-9.]/g, ''));
    const currentTotalCents = isNaN(currentTotalParsed) ? 0 : Math.round(currentTotalParsed * 100);
    
    const newTotalCents = Math.max(0, currentTotalCents + (itemPriceCents * change));
    totalContainer.innerText = this.formatMoney(newTotalCents);
  }

  async addFromForm(form) {
    const formData = new FormData(form);
    const submitBtn = form.querySelector('button[type="submit"]');
    if (!submitBtn) return;
    
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = window.cartStrings.updating || 'UPDATING...';

    this.pendingRequests++;

    try {
      await fetch('/cart/add.js', { method: 'POST', body: formData });
      this.open();
    } catch (err) {
      console.error('Cart update failure:', err);
    } finally {
      this.pendingRequests--;
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  }

  open() {
    const drawer = this.getDrawer();
    if (drawer) {
      drawer.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.refresh();
    }
  }

  close() {
    const drawer = this.getDrawer();
    if (drawer) {
      drawer.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  render(cart, force = false) {
    // State Guard: Only render if no changes are currently pending/debouncing
    if (!force && (this.pendingRequests > 1 || this.refreshTimeout)) {
      return; 
    }

    const itemsContainer = document.getElementById('CartDrawerItems');
    const totalContainer = document.getElementById('CartDrawerTotal');
    if (!itemsContainer) return;

    if (cart.item_count === 0) {
      const emptyMsg = window.cartStrings.emptyMessage || 'Your cart is empty.';
      const continueBtn = window.cartStrings.continueShopping || 'Continue Shopping';
      
      itemsContainer.innerHTML = `
        <div data-testid="cart-drawer-empty" class="flex flex-col items-center justify-center h-full text-center space-y-6">
          <span class="material-symbols-outlined text-ghost text-6xl">shopping_cart_off</span>
          <p class="text-ghost font-body uppercase tracking-widest">${emptyMsg}</p>
          <button class="cart-drawer-close px-8 py-3 bg-bone text-black font-black uppercase text-xs tracking-widest hover:bg-white transition-all">${continueBtn}</button>
        </div>
      `;
    } else {
      itemsContainer.innerHTML = cart.items.map(item => `
        <div data-testid="cart-item" data-price-cents="${item.price}" class="cart-item-row flex gap-6 border-b border-outline-variant/10 pb-8 transition-opacity duration-300">
          <div class="w-24 h-32 bg-surface-container-high shrink-0 overflow-hidden">
            <img src="${item.image}" alt="${item.title}" class="w-full h-full object-cover">
          </div>
          <div class="grow flex flex-col justify-between">
            <div>
              <h4 data-testid="cart-item-title" class="text-bone font-headline font-black tracking-tighter text-lg uppercase">${item.product_title}</h4>
              <p class="text-ghost text-[10px] tracking-widest uppercase mt-1">${item.variant_title || ''}</p>
            </div>
            <div class="flex justify-between items-end">
              <span class="item-price text-bone font-headline font-bold">${this.formatMoney(item.price)}</span>
              <div class="flex items-center border border-outline-variant/20 px-2">
                <button data-testid="cart-item-qty-minus" class="qty-minus p-1 text-ghost hover:text-bone" data-key="${item.key}">-</button>
                <span data-testid="cart-item-qty" class="qty-display px-4 text-xs font-bold text-bone">${item.quantity}</span>
                <button data-testid="cart-item-qty-plus" class="qty-plus p-1 text-ghost hover:text-bone" data-key="${item.key}">+</button>
              </div>
            </div>
          </div>
        </div>
      `).join('');
    }
    
    if (totalContainer) {
      totalContainer.innerText = this.formatMoney(cart.total_price);
    }
  }

  async updateQty(key, quantity) {
    this.pendingRequests++;

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      this.render(cart);
    } catch (err) {
      console.error('Tactical quantity adjustment failed:', err);
      this.refresh();
    } finally {
      this.pendingRequests--;
    }
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.moneyFormat);
    }
    const format = window.moneyFormat || '${{amount}}';
    const dollars = (cents / 100).toFixed(2);
    if (format.includes('{{amount_no_decimals}}')) {
      return format.replace('{{amount_no_decimals}}', Math.round(cents / 100));
    }
    if (format.includes('{{amount_with_comma_separator}}')) {
      return format.replace('{{amount_with_comma_separator}}', dollars.replace('.', ','));
    }
    if (format.includes('{{amount}}')) {
      return format.replace('{{amount}}', dollars);
    }
    return '$' + dollars;
  }
}

window.vultureCart = new VultureCart();
